export const PRICE_CHART_RANGE_OPTIONS = [
  { id: "max", label: "Max" },
  { id: "5y", label: "5Y" },
  { id: "3y", label: "3Y" },
  { id: "1y", label: "1Y" },
  { id: "6m", label: "6M" },
  { id: "3m", label: "3M" },
  { id: "1m", label: "1M" },
];

export const DEFAULT_PRICE_CHART_RANGE = "max";

export const parsePriceChartDate = (dateStr) => {
  if (!dateStr) {
    return null;
  }

  const normalized = String(dateStr).substr(0, 10);
  const [year, month, day] = normalized.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

export const getRangeCutoffDate = (rangeId, referenceDate = new Date()) => {
  if (!rangeId || rangeId === "max") {
    return null;
  }

  const cutoff = new Date(referenceDate);

  switch (rangeId) {
    case "5y":
      cutoff.setFullYear(cutoff.getFullYear() - 5);
      return cutoff;
    case "3y":
      cutoff.setFullYear(cutoff.getFullYear() - 3);
      return cutoff;
    case "1y":
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      return cutoff;
    case "6m":
      cutoff.setMonth(cutoff.getMonth() - 6);
      return cutoff;
    case "3m":
      cutoff.setMonth(cutoff.getMonth() - 3);
      return cutoff;
    case "1m":
      cutoff.setMonth(cutoff.getMonth() - 1);
      return cutoff;
    default:
      return null;
  }
};

export const filterPriceSeriesByRange = (
  series = [],
  rangeId,
  referenceDate = new Date()
) => {
  const cutoff = getRangeCutoffDate(rangeId, referenceDate);

  if (!cutoff) {
    return [...series];
  }

  return series.filter((item) => {
    const pointDate = parsePriceChartDate(item?.date);
    return pointDate && pointDate >= cutoff;
  });
};

export const normalizePricesByDate = (prices) => {
  if (prices && typeof prices === "object" && !Array.isArray(prices)) {
    return {
      market: prices.market || [],
      trade: prices.trade || [],
    };
  }

  return { market: [], trade: [] };
};

export const filterPricesByRange = (prices, rangeId, referenceDate = new Date()) => {
  const source = normalizePricesByDate(prices);

  return {
    market: filterPriceSeriesByRange(source.market, rangeId, referenceDate),
    trade: filterPriceSeriesByRange(source.trade, rangeId, referenceDate),
  };
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getAllPriceDates = (prices) =>
  [...normalizePricesByDate(prices).market, ...normalizePricesByDate(prices).trade]
    .map((item) => parsePriceChartDate(item?.date))
    .filter(Boolean);

export const getEarliestPriceDate = (prices) => {
  const dates = getAllPriceDates(prices);

  if (!dates.length) {
    return null;
  }

  return new Date(Math.min(...dates.map((date) => date.getTime())));
};

export const getLatestPriceDate = (prices) => {
  const dates = getAllPriceDates(prices);

  if (!dates.length) {
    return null;
  }

  return new Date(Math.max(...dates.map((date) => date.getTime())));
};

export const getChartXDomain = (rangeId, prices, referenceDate = new Date()) => {
  const end = new Date(referenceDate);
  end.setHours(23, 59, 59, 999);

  const cutoff = getRangeCutoffDate(rangeId, referenceDate);

  if (!cutoff) {
    const earliest = getEarliestPriceDate(prices);

    if (!earliest) {
      return null;
    }

    return [earliest, end];
  }

  return [cutoff, end];
};

const RANGE_VIEW_LABELS = {
  "5y": "5-year",
  "3y": "3-year",
  "1y": "1-year",
  "6m": "6-month",
  "3m": "3-month",
  "1m": "1-month",
};

export const formatTradingHistorySpan = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return null;
  }

  const totalMonths =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());

  if (totalMonths >= 12) {
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (months === 0) {
      return years === 1 ? "1 year" : `${years} years`;
    }

    const yearLabel = years === 1 ? "1 year" : `${years} years`;
    const monthLabel = months === 1 ? "1 month" : `${months} months`;
    return `${yearLabel} ${monthLabel}`;
  }

  if (totalMonths >= 1) {
    return totalMonths === 1 ? "1 month" : `${totalMonths} months`;
  }

  const days = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / MS_PER_DAY));
  return days === 1 ? "1 day" : `${days} days`;
};

export const getLimitedHistoryNotice = (
  prices,
  rangeId,
  referenceDate = new Date()
) => {
  if (!rangeId || rangeId === "max") {
    return null;
  }

  const cutoff = getRangeCutoffDate(rangeId, referenceDate);

  if (!cutoff) {
    return null;
  }

  const datesInRange = getAllPriceDates(prices).filter(
    (date) => date >= cutoff && date <= referenceDate
  );

  if (!datesInRange.length) {
    return null;
  }

  const earliest = new Date(Math.min(...datesInRange.map((date) => date.getTime())));
  const latest = new Date(Math.max(...datesInRange.map((date) => date.getTime())));
  const gapBeforeDataMs = earliest.getTime() - cutoff.getTime();

  if (gapBeforeDataMs < 7 * MS_PER_DAY) {
    return null;
  }

  const spanLabel = formatTradingHistorySpan(earliest, latest);
  const viewLabel = RANGE_VIEW_LABELS[rangeId] || rangeId;

  return `Only ${spanLabel} of trading history is available in this ${viewLabel} view. Earlier periods had no recorded trades.`;
};

export const isValidListingLink = (link) =>
  typeof link === "string" && link.trim().length > 0;

export const formatPriceChartPrice = (amountPence, currency = "GBP") => {
  if (amountPence === null || amountPence === undefined) {
    return "N/A";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amountPence / 100);
};

export const formatPriceChartDate = (dateStr) => {
  const date = parsePriceChartDate(dateStr);

  if (!date) {
    return "N/A";
  }

  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

export const clampTooltipPosition = (left, top, tooltipWidth, tooltipHeight) => {
  const margin = 12;
  const maxLeft = window.innerWidth - tooltipWidth - margin;
  const maxTop = window.innerHeight - tooltipHeight - margin;

  return {
    left: Math.min(Math.max(margin, left), Math.max(margin, maxLeft)),
    top: Math.min(Math.max(margin, top), Math.max(margin, maxTop)),
  };
};
